import MonthlyAiUsage from "../StitchEditor/MonthlyAiUsage";
import React, { useState, useContext, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import { 
  cloudUploadOutline,
  personOutline,
  appsOutline,
  imageOutline,
  logOutOutline,
  playCircleOutline,
  settingsOutline
} from 'ionicons/icons';
import { Button, Upload } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExpand,
  faWandSparkles,
} from '@fortawesome/free-solid-svg-icons';
import { AppContext } from '../../contexts/AppContext';
import { queueUploadImage } from '../../lib/upload/queueUploadImage';
import { useAppDispatch } from '../../redux/store';
import { useAuth } from '../../contexts/AuthContext';
import { signOutUser } from '../../lib/firebase/firebase';
import { useTutorial } from '../Tutorial/TutorialProvider';
import { ReactComponent as SVGTraceLogo } from '../../assets/logo1.svg';
import { features, isEmbroideryTrace, tutorialSampleImage, tutorialSampleFileName } from '../../config/appVariant';
import branding from '../../config/branding';
import SettingsPage from '../SettingsPage';
import './MobileSidebar.css';

// Sample image loading function (same as desktop)
const loadSampleImage = async () => {
  console.log('🎯 Loading sample image...');
  
  try {
    const sampleImagePath = tutorialSampleImage;
    const response = await fetch(sampleImagePath);
    
    if (response.ok) {
      const blob = await response.blob();
      
      if (blob.size > 0) {
        const imageData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (e) => reject(e);
          reader.readAsDataURL(blob);
        });
        
        console.log('✅ Sample image loaded successfully');
        return imageData;
      }
    }
  } catch (error) {
    console.error('❌ Error loading sample image:', error);
  }
  
  // Fallback: Generate a simple sample image
  console.log('⚠️ Using fallback sample image');
  const canvas = document.createElement('canvas');
  canvas.width = 200;
  canvas.height = 200;
  const ctx = canvas.getContext('2d');
  
  // White background
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, 200, 200);
  
  // Draw a simple star
  ctx.fillStyle = '#667eea';
  ctx.beginPath();
  ctx.moveTo(100, 20);
  ctx.lineTo(120, 70);
  ctx.lineTo(180, 70);
  ctx.lineTo(135, 110);
  ctx.lineTo(155, 180);
  ctx.lineTo(100, 140);
  ctx.lineTo(45, 180);
  ctx.lineTo(65, 110);
  ctx.lineTo(20, 70);
  ctx.lineTo(80, 70);
  ctx.closePath();
  ctx.fill();
  
  return canvas.toDataURL('image/png');
};

const MobileSidebar = ({ isOpen, setIsOpen, onNavigate, onResizeImage, onGenerateImage, setImage64, setFileName, onBillingClick }) => {
  const [activeItem, setActiveItem] = useState('home');
  const [dragOver, setDragOver] = useState(false);
  const [showSettingsPage, setShowSettingsPage] = useState(false);
  
  const dispatch = useAppDispatch();
  const { setImageSource, tutorialMode } = useContext(AppContext);
  const { user } = useAuth();
  const { startTutorial, hasCompletedTutorial } = useTutorial();

  const handleItemClick = useCallback((itemId) => {
    setActiveItem(itemId);
    if (itemId !== 'create' && onNavigate) {
      onNavigate(itemId);
    }
  }, [onNavigate]);

  const handleFileUpload = (file) => {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      console.log("Unsupported file format");
      return Upload.LIST_IGNORE;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      queueUploadImage(dispatch, e.target.result);
      if (setImage64) {
        setImage64(e.target.result);
      }
      if (setFileName) {
        setFileName(file.name);
      }
    };
    setImageSource("uploaded");
    reader.readAsDataURL(file);
    return false;
  };

  const handleLogout = async () => {
    try {
      await signOutUser();
      // Redirect to sign in page after logout
      window.location.href = '/signin';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleCourseClick = useCallback(() => {
    setIsOpen(false);
    window.open('https://www.embroiderytrace.com/help/course/', '_blank', 'noopener,noreferrer');
  }, [setIsOpen]);

  const handleSampleImageClick = useCallback(async () => {
    try {
      const imageData = await loadSampleImage();
      if (imageData) {
        queueUploadImage(dispatch, imageData);
        if (setImage64) {
          setImage64(imageData);
        }
        if (setFileName) {
          setFileName(tutorialSampleFileName);
        }
        setImageSource("uploaded");
      }
    } catch (error) {
      console.error('Error loading sample image:', error);
    }
  }, [dispatch, setImage64, setFileName, setImageSource]);

  return (
    <>
      {/* Mobile Bottom Bar */}
      <div className="mobile-bottom-bar">
        {/* Create Image */}
        {!isEmbroideryTrace && <div
          className="mobile-bottom-item" 
          onClick={onGenerateImage}
          data-action="generate"
          data-onboarding-target="ai-generate"
        >
          <FontAwesomeIcon icon={faWandSparkles} />
          <span>Create</span>
        </div>}

        {/* Primary Upload Button - Centered */}
        <Upload.Dragger
          name="file"
          listType="text"
          accept="image/jpeg, image/png, image/webp"
          beforeUpload={handleFileUpload}
          showUploadList={false}
          multiple={false}
          className={`mobile-bottom-upload primary ${dragOver ? 'mobile-upload-dragover' : ''}`}
          onDrop={() => setDragOver(false)}
          onDragEnter={() => setDragOver(true)}
          onDragLeave={() => setDragOver(false)}
          data-testid="upload-area"
          data-onboarding-target="upload"
          data-tutorial="upload-button"
        >
          <div className="mobile-bottom-item primary">
            <IonIcon icon={cloudUploadOutline} />
            <span>Upload</span>
          </div>
        </Upload.Dragger>

        {/* More Options */}
        <div 
          className={`mobile-bottom-item ${isOpen ? 'active' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <IonIcon icon={appsOutline} />
          <span>More</span>
        </div>
      </div>

      {/* Expanded More Menu */}
      {isOpen && (
        <>
          <div className="mobile-more-menu">
            {/* Logo */}
            <div className="mobile-logo-section">
              {features.svgEditor ? (
                <SVGTraceLogo className="mobile-logo" />
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <img src={branding.logoIcon} alt={branding.appName} style={{ height: "28px", objectFit: "contain" }} />
                  <span style={{ fontSize: "18px", fontWeight: 600, fontFamily: "Clash Grotesk, sans-serif", color: "#1e293b" }}>{branding.appName}</span>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="mobile-more-divider"></div>

            {/* Primary Actions */}
            <div className="mobile-more-section">
              {isEmbroideryTrace && (
                <div
                  className="mobile-more-item primary-action"
                  onClick={handleCourseClick}
                  data-course="true"
                >
                  <IonIcon icon={playCircleOutline} />
                  <span>Watch Video Course</span>
                </div>
              )}

              <div 
                className="mobile-more-item primary-action tutorial-only-button" 
                onClick={handleSampleImageClick} 
                data-sample="true"
                style={{ display: tutorialMode ? 'none' : 'flex' }}
              >
                <IonIcon icon={imageOutline} />
                <span>Use Sample Image</span>
              </div>
              
              {features.svgEditor && (
                <div className="mobile-more-item primary-action" onClick={onResizeImage}>
                  <FontAwesomeIcon icon={faExpand} />
                  <span>Resize Image</span>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="mobile-more-divider"></div>

            {/* Settings */}
            <div className="mobile-more-section">
              <div className="mobile-more-item" onClick={() => {
                setIsOpen(false);
                setShowSettingsPage(true);
              }}>
                <IonIcon icon={settingsOutline} />
                <span>Settings</span>
              </div>
              
              {isEmbroideryTrace && <MonthlyAiUsage />}
              <div className="mobile-more-item" onClick={() => {
                setIsOpen(false);
                if (onBillingClick) onBillingClick();
              }}>
                <IonIcon icon={personOutline} />
                <span>My Subscription</span>
              </div>

              <div className="mobile-more-item logout-item" onClick={handleLogout}>
                <IonIcon icon={logOutOutline} />
                <span>Log Out</span>
              </div>
              
            </div>
          </div>

          {/* Overlay */}
          <div className="mobile-more-overlay" onClick={() => setIsOpen(false)} />
        </>
      )}

      {/* Full Settings Page Overlay */}
      {showSettingsPage && (
        <div className="settings-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowSettingsPage(false); }}>
          <SettingsPage onClose={() => setShowSettingsPage(false)} />
        </div>
      )}
    </>
  );
};

export default MobileSidebar;
